// FLAGS: personal-data
// Edit-form state, validation and diffing: carries the employer ids and the per-session expert list.
/**
 * Pure form state + validation + diffing for the Edit Event flow (OI-25024,
 * non-reschedule slice). Kept free of React so the rules (required title,
 * positive capacity, what changed → which PATCHes) are unit-testable.
 */
import {
  ContentLanguage,
  SessionExpertInput,
  SpaceEvent,
  SpaceEventType,
  SpaceSession,
  UpdateSessionRequest,
  UpdateSpaceEventRequest,
} from "@src/models/spaces";
import {
  TranslationDraft,
  TranslationSessionDraft,
  toEventTranslationPayload,
} from "@src/components/spaces/translationDrafts";
import { isValidHttpUrl } from "@src/utils/url";

export const MAX_INSTRUCTIONS_LENGTH = 2000;

/** Minutes between two naive-local ISO datetimes (tz-independent difference). */
const durationBetween = (startsAt: string, endsAt: string): number =>
  Math.max(1, Math.round((Date.parse(endsAt) - Date.parse(startsAt)) / 60000));

export interface EditSessionFormState {
  id: string;
  /** Raw name input; blank falls back to the event title server-side. */
  name: string;
  /** Raw input; must parse to a positive integer. */
  capacity: string;
  sessionInstructions: string;
  /** Local date, "YYYY-MM-DD" (a change is a reschedule). */
  date: string;
  /** Local start time, "HH:mm". */
  startTime: string;
  durationMinutes: number;
  timeZone: string;
  experts: SessionExpertInput[];
  /** Scheduler note sent in the reschedule email; only meaningful on a time change. */
  rescheduleInfo: string;
  /** Cancelled/completed sessions are read-only (AC6). */
  editable: boolean;
}

// The draft shapes moved to translationDrafts.ts when the create form started sharing them
// (OI-25388); re-exported so existing importers of this module keep working.
export type { TranslationDraft, TranslationSessionDraft };

export interface EditEventFormState {
  title: string;
  description: string;
  /** Every company the event is open to; removing all of them is rejected. */
  employerIds: string[];
  /**
   * Audience name for the public landing page; blank uses the single company's
   * own name, and is required once there is more than one company.
   */
  employerDisplayName: string;
  /** Event type is not editable; carried so masterclass-only fields can render/validate. */
  eventType: SpaceEventType;
  /** Post-session survey link; masterclass-only, blank clears it. */
  surveyUrl: string;
  /**
   * Which language the fields above are written in. Empty for events created
   * before multilingual content; the scheduler must pick one before any
   * translation can be saved.
   */
  languageCode: ContentLanguage | "";
  /** One draft per non-source language the scheduler has opened. */
  translations: TranslationDraft[];
  sessions: EditSessionFormState[];
}

export interface EditSessionErrors {
  capacity?: string;
  experts?: string;
}

export interface EditFormErrors {
  title?: string;
  surveyUrl?: string;
  employerIds?: string;
  employerDisplayName?: string;
  languageCode?: string;
  /** Keyed by session id (stable across filtering, unlike index). */
  sessions: Record<string, EditSessionErrors>;
}

const isSessionEditable = (session: SpaceSession, now: Date): boolean =>
  session.status === "scheduled" && new Date(session.endsAt) > now;

/**
 * An event is cancelled when every session is — there is no event-level status
 * column; cancellation is derived. Cancelled events are read-only.
 */
export const isEventCancelled = (event: SpaceEvent): boolean =>
  event.sessions.length > 0 &&
  event.sessions.every((session) => session.status === "cancelled");

/** Rebuilds the per-language drafts from what is stored, so opening a language shows its saved copy. */
const draftsFrom = (event: SpaceEvent): TranslationDraft[] => {
  const languages = new Set<ContentLanguage>([
    ...event.translations.map((translation) => translation.languageCode),
    ...event.sessions.flatMap((session) =>
      session.translations.map((translation) => translation.languageCode),
    ),
  ]);

  return [...languages].map((languageCode) => {
    const eventTranslation = event.translations.find(
      (translation) => translation.languageCode === languageCode,
    );
    const sessions: Record<string, TranslationSessionDraft> = {};
    event.sessions.forEach((session) => {
      const translation = session.translations.find(
        (entry) => entry.languageCode === languageCode,
      );
      sessions[session.id] = {
        name: translation?.title ?? "",
        sessionInstructions: translation?.sessionInstructions ?? "",
      };
    });
    return {
      languageCode,
      title: eventTranslation?.title ?? "",
      description: eventTranslation?.description ?? "",
      sessions,
    };
  });
};

export const fromEvent = (
  event: SpaceEvent,
  now: Date,
): EditEventFormState => ({
  title: event.title,
  description: event.description ?? "",
  employerIds: event.employers.map((employer) => employer.id),
  employerDisplayName: event.employerDisplayName ?? "",
  eventType: event.eventType,
  surveyUrl: event.surveyUrl ?? "",
  languageCode: event.languageCode ?? "",
  translations: draftsFrom(event),
  sessions: event.sessions.map((session) => ({
    id: session.id,
    // A defaulted title is a copy of the event title, not a name the scheduler
    // chose — show it blank so saving doesn't flip it to an explicit name
    // (which would stop it following event renames).
    name: session.titleIsDefaulted ? "" : session.title,
    capacity: session.capacity === null ? "" : String(session.capacity),
    sessionInstructions: session.sessionInstructions ?? "",
    // startsAt/endsAt are naive local wall-clock (+ timeZone) — parse directly.
    date: session.startsAt.slice(0, 10),
    startTime: session.startsAt.slice(11, 16),
    durationMinutes: durationBetween(session.startsAt, session.endsAt),
    timeZone: session.timeZone,
    experts: session.experts.map((expert) => ({
      userId: expert.userId,
      role: expert.role,
    })),
    rescheduleInfo: "",
    editable: isSessionEditable(session, now),
  })),
});

export const validateEditForm = (form: EditEventFormState): EditFormErrors => {
  const errors: EditFormErrors = { sessions: {} };
  if (!form.title.trim()) {
    errors.title = "Title is required.";
  }
  if (
    form.eventType === "masterclass" &&
    form.surveyUrl.trim() &&
    !isValidHttpUrl(form.surveyUrl)
  ) {
    errors.surveyUrl = "Enter a full link starting with https://";
  }
  // The backend rejects translations with no source language declared (a
  // translation has to be a translation *of* something). Caught here so the
  // scheduler sees it on the field rather than as a failed save.
  if (form.employerIds.length === 0) {
    errors.employerIds = "Pick at least one company.";
  }
  // Same rule as creation: with several companies nothing else holds the name of
  // the group the event addresses.
  if (form.employerIds.length > 1 && !form.employerDisplayName.trim()) {
    errors.employerDisplayName =
      "Give the event a public name when it is for more than one company.";
  }
  if (!form.languageCode && hasAnyTranslationContent(form)) {
    errors.languageCode =
      "Choose the language this event's content is written in before adding a translation.";
  }
  form.sessions
    .filter((session) => session.editable)
    .forEach((session) => {
      const sessionErrors: EditSessionErrors = {};
      const capacity = Number(session.capacity);
      if (!session.capacity || !Number.isInteger(capacity) || capacity < 1) {
        sessionErrors.capacity = "Capacity must be a positive whole number.";
      }
      if (!session.experts.some((expert) => expert.role === "host")) {
        sessionErrors.experts = "Each session needs at least one host.";
      }
      if (Object.keys(sessionErrors).length > 0) {
        errors.sessions[session.id] = sessionErrors;
      }
    });
  return errors;
};

export const hasEditErrors = (errors: EditFormErrors): boolean =>
  Boolean(errors.title) ||
  Boolean(errors.surveyUrl) ||
  Boolean(errors.employerIds) ||
  Boolean(errors.employerDisplayName) ||
  Boolean(errors.languageCode) ||
  Object.keys(errors.sessions).length > 0;

/** Whether any language draft carries a non-blank field. An empty draft saves nothing. */
export const hasAnyTranslationContent = (form: EditEventFormState): boolean =>
  form.translations.some(
    (draft) =>
      draft.title.trim() !== "" ||
      draft.description.trim() !== "" ||
      Object.values(draft.sessions).some(
        (session) =>
          session.name.trim() !== "" ||
          session.sessionInstructions.trim() !== "",
      ),
  );

export interface EditDiff {
  event: UpdateSpaceEventRequest | null;
  sessions: Array<{ sessionId: string; request: UpdateSessionRequest }>;
}

/**
 * Computes which PATCHes to send: the event only when title/description
 * changed, and each editable session whose fields changed. Untouched
 * entities get no request at all — saving with no changes is a no-op.
 */
export const diffAgainst = (
  form: EditEventFormState,
  event: SpaceEvent,
): EditDiff => {
  const title = form.title.trim();
  const description = form.description.trim() || null;
  // Masterclass-only; a group session always PATCHes null (the backend rejects a value).
  const surveyUrl =
    form.eventType === "masterclass" ? form.surveyUrl.trim() || null : null;
  const employerDisplayName = form.employerDisplayName.trim() || null;
  const employerIds = [...form.employerIds].sort();
  const languageCode = form.languageCode || null;

  // The event PATCH replaces the whole translation set, so it always carries the
  // current drafts. Blank fields become null: the reader falls back per field to
  // the source copy, and a draft with nothing in it is dropped rather than saved
  // as an empty row.
  const translations = toEventTranslationPayload(form.translations);

  const sessionTranslations = form.translations.flatMap((draft) =>
    Object.entries(draft.sessions)
      .map(([sessionId, session]) => ({
        sessionId,
        languageCode: draft.languageCode,
        title: session.name.trim() || null,
        sessionInstructions: session.sessionInstructions.trim() || null,
      }))
      .filter(
        (entry) => entry.title !== null || entry.sessionInstructions !== null,
      ),
  );

  const eventChanged =
    title !== event.title ||
    description !== (event.description ?? null) ||
    surveyUrl !== (event.surveyUrl ?? null) ||
    employerDisplayName !== (event.employerDisplayName ?? null) ||
    employerIds.join() !==
      event.employers
        .map((employer) => employer.id)
        .sort()
        .join() ||
    languageCode !== (event.languageCode ?? null) ||
    translationsChanged(translations, sessionTranslations, event);

  const sessions = form.sessions
    .filter((session) => session.editable)
    .flatMap((session) => {
      const original = event.sessions.find((s) => s.id === session.id);
      if (!original) return [];

      const request: UpdateSessionRequest = {
        name: session.name.trim() || null,
        capacity: Number(session.capacity),
        sessionInstructions: session.sessionInstructions.trim() || null,
        date: session.date,
        startTime: session.startTime,
        durationMinutes: session.durationMinutes,
        timeZone: session.timeZone,
        experts: session.experts.map((expert) => ({
          userId: expert.userId,
          role: expert.role,
        })),
        rescheduleAdditionalInformation: session.rescheduleInfo.trim() || null,
      };

      const originalDuration = durationBetween(
        original.startsAt,
        original.endsAt,
      );
      const timeChanged =
        session.date !== original.startsAt.slice(0, 10) ||
        session.startTime !== original.startsAt.slice(11, 16) ||
        session.durationMinutes !== originalDuration ||
        session.timeZone !== original.timeZone;
      const expertsChanged = !sameExperts(session.experts, original.experts);
      const changed =
        (request.name ?? event.title) !== original.title ||
        request.capacity !== original.capacity ||
        request.sessionInstructions !==
          (original.sessionInstructions ?? null) ||
        timeChanged ||
        expertsChanged;

      return changed ? [{ sessionId: session.id, request }] : [];
    });

  return {
    event: eventChanged
      ? {
          title,
          description,
          surveyUrl,
          employerIds,
          employerDisplayName,
          languageCode,
          translations,
          sessionTranslations,
        }
      : null,
    sessions,
  };
};

/**
 * Whether the submitted translation set differs from what is stored. Compared as
 * sorted, normalised lists rather than field by field, since the set is a full
 * replace and a language can be added or removed as well as edited.
 */
const translationsChanged = (
  translations: UpdateSpaceEventRequest["translations"],
  sessionTranslations: UpdateSpaceEventRequest["sessionTranslations"],
  event: SpaceEvent,
): boolean => {
  const eventKey = (entries: UpdateSpaceEventRequest["translations"]) =>
    entries
      .map((entry) =>
        [entry.languageCode, entry.title, entry.description].join("\u0000"),
      )
      .sort()
      .join("\u0001");

  const sessionKey = (
    entries: UpdateSpaceEventRequest["sessionTranslations"],
  ) =>
    entries
      .map((entry) =>
        [
          entry.sessionId,
          entry.languageCode,
          entry.title,
          entry.sessionInstructions,
        ].join("\u0000"),
      )
      .sort()
      .join("\u0001");

  const storedSessionTranslations = event.sessions.flatMap((session) =>
    session.translations.map((translation) => ({
      sessionId: session.id,
      languageCode: translation.languageCode,
      title: translation.title,
      sessionInstructions: translation.sessionInstructions,
    })),
  );

  return (
    eventKey(translations) !== eventKey(event.translations) ||
    sessionKey(sessionTranslations) !== sessionKey(storedSessionTranslations)
  );
};

/** Same experts iff the same people hold the same host/non-host status. */
const sameExperts = (
  a: SessionExpertInput[],
  b: Array<{ userId: string; role: SessionExpertInput["role"] }>,
): boolean => {
  if (a.length !== b.length) return false;
  const key = (userId: string, role: SessionExpertInput["role"]) =>
    `${userId}:${role === "host" ? "host" : "guest"}`;
  const setA = new Set(a.map((expert) => key(expert.userId, expert.role)));
  return b.every((expert) => setA.has(key(expert.userId, expert.role)));
};

/**
 * Whether a session's timing differs from the stored session — used to reveal
 * the "reason for rescheduling" field, since only a time change notifies
 * attendees (email 177).
 */
export const isSessionRescheduled = (
  session: EditSessionFormState,
  original: SpaceSession,
): boolean =>
  session.date !== original.startsAt.slice(0, 10) ||
  session.startTime !== original.startsAt.slice(11, 16) ||
  session.durationMinutes !==
    durationBetween(original.startsAt, original.endsAt) ||
  session.timeZone !== original.timeZone;

export interface RescheduledSession {
  session: EditSessionFormState;
  original: SpaceSession;
  /** 1-based position in the form, for "Session N" fallback labels. */
  index: number;
}

/**
 * The editable sessions whose timing was changed — the ones a save will
 * reschedule (and email attendees about). Drives the confirmation dialog
 * shown before those saves go out.
 */
export const rescheduledSessions = (
  form: EditEventFormState,
  event: SpaceEvent,
): RescheduledSession[] =>
  form.sessions.flatMap((session, index) => {
    if (!session.editable) return [];
    const original = event.sessions.find((s) => s.id === session.id);
    if (!original || !isSessionRescheduled(session, original)) return [];
    return [{ session, original, index: index + 1 }];
  });
